/**
 * justify — the wall's row layout, pure. `(tiles, containerWidth, targetHeight, gap) → rows[]`, per
 * plan d § Files: "row widths exact, no reflow on identical input." A classic justified-gallery
 * accumulate-then-scale pass: tiles are appended to the current row until its natural width (every
 * tile scaled to `targetHeight`, gaps included) reaches the container, then the whole row is
 * rescaled so its total width lands exactly on `containerWidth` — the row's HEIGHT is what moves,
 * never a crop, and never past 1.6x the target (a row closed early by the one-wide-per-row rule
 * ends short rather than towering; review item S13). No randomness, no DOM reads: same inputs
 * always produce the same rows, which is what "no reflow on identical input" means for a function
 * with no state of its own.
 *
 * One authored exception from the storage plan's § Composition (`2026_08_27_wall_storage.md`): one
 * wide tile (≥16:9) per row at most — forced closed before a second one joins.
 */

/** Everything the layout reads off a cell — a wall tile (`TileSummary`) satisfies it, and so does
 *  the wall's own from-scratch cell, which has stored `Take.width/height` and no tile behind it. */
export type LayoutTile = {width: number; height: number}

export type JustifiedCell<T extends LayoutTile = LayoutTile> = {tile: T; width: number}
export type JustifiedRow<T extends LayoutTile = LayoutTile> = {
    height: number
    cells: JustifiedCell<T>[]
    /** False for the ONE row this packer can leave short of the container: the trailing row, when
     *  the tiles ran out before it filled (`flush`'s own `isTrailing` branch). A row closed early by
     *  the one-wide-per-row rule is a deliberate composition and reads `true` — "full" here means
     *  "not the ragged tail", not "occupies exactly `containerWidth`". Nothing in an endless wall
     *  reads it; `boundedRows` does. */
    full: boolean
}

const WIDE_RATIO = 16 / 9
/** No row is ever taller than this, whatever forces it to close early — see `flush`. */
const MAX_ROW_SCALE = 1.6

function aspect(tile: LayoutTile): number {
    return tile.height > 0 ? tile.width / tile.height : 1
}

function scaleRow<T extends LayoutTile>(tiles: T[], height: number): JustifiedCell<T>[] {
    return tiles.map((tile) => ({tile, width: aspect(tile) * height}))
}

export function justifyRows<T extends LayoutTile>(
    tiles: T[],
    containerWidth: number,
    targetHeight = 220,
    gap = 8,
): JustifiedRow<T>[] {
    if (containerWidth <= 0 || tiles.length === 0) return []

    const rows: JustifiedRow<T>[] = []
    let current: T[] = []
    let ratioSum = 0
    let wideCount = 0

    function flush(isTrailing: boolean) {
        if (current.length === 0) return

        const gapsWidth = gap * (current.length - 1)
        const availableWidth = containerWidth - gapsWidth
        const naturalWidth = ratioSum * targetHeight + gapsWidth

        // The trailing (last, possibly under-filled) row keeps its natural height rather than being
        // stretched to fill the container — an incomplete row reads as "more below," not a
        // deliberately short one. A row that already reached or exceeded the container width always
        // scales exactly to it, trailing or not.
        const stretched = !isTrailing || naturalWidth >= containerWidth ? availableWidth / ratioSum : targetHeight

        // A row that closes with fewer tiles than it wanted — the one-wide-per-row rule below
        // forces this whenever a second ≥16:9 tile arrives — would otherwise be stretched to the
        // container on its own: a lone 21:9 tile at a 1200px container reads 514px tall, more than
        // twice the target, and the wall loses its beat. Cap it at `MAX_ROW_SCALE` instead and let
        // the row end short.
        const height = Math.min(stretched, targetHeight * MAX_ROW_SCALE)

        rows.push({height, cells: scaleRow(current, height), full: !isTrailing || naturalWidth >= containerWidth})
        current = []
        ratioSum = 0
        wideCount = 0
    }

    for (let i = 0; i < tiles.length; i++) {
        const tile = tiles[i]
        const ratio = aspect(tile)
        const isWide = ratio >= WIDE_RATIO

        // One ≥16:9 tile per row at most — force the row closed before a second one joins.
        if (isWide && wideCount >= 1) flush(false)

        current.push(tile)
        ratioSum += ratio
        if (isWide) wideCount += 1

        const gapsWidth = gap * (current.length - 1)
        const naturalWidth = ratioSum * targetHeight + gapsWidth
        if (naturalWidth >= containerWidth) flush(false)
    }
    flush(true)

    return rows
}

/**
 * A finite wall's ending (plan `2026_09_17_ovid_one_wall.md` § Decided 5) — the same justified rows,
 * cut to "a short coda, full rows only, at every breakpoint".
 *
 * Two cuts, in this order:
 *  1. the coda — every row that lies wholly at or after cell index `codaFrom` — is kept to at most
 *     `codaRows` rows. A row holding any cell before `codaFrom` is never cut, so the band's last row
 *     stands whole, completed by coda cells when it is mixed. The cut is made on ROWS rather than on
 *     a tile count because the column count is a function of the measured container width: the wall
 *     must end the same depth past the band at desktop and at phone width.
 *  2. a trailing row that is not `full` is dropped — the "no ragged row anywhere" rule. Only the
 *     packer's own last row can be short, so this removes at most one, and only when the coda cut
 *     above did not already land on a full interior row.
 */
export function boundedRows<T extends LayoutTile>(
    rows: readonly JustifiedRow<T>[],
    {codaFrom, codaRows}: {codaFrom: number; codaRows: number},
): JustifiedRow<T>[] {
    const kept: JustifiedRow<T>[] = []
    let firstCellIndex = 0
    let codaSeen = 0
    for (const row of rows) {
        if (firstCellIndex >= codaFrom) {
            if (codaSeen >= codaRows) break
            codaSeen += 1
        }
        kept.push(row)
        firstCellIndex += row.cells.length
    }
    if (kept.length > 0 && !kept[kept.length - 1].full) kept.pop()
    return kept
}
